import { Injectable, Inject } from '@angular/core';
import { IssueLevel } from '@icc/shared/helpers';
import { ProfilesService, ValidationService } from '@icc/construction-elements/configurator';
import { SplitFrameService } from './split-frame.service';
import {APP_CONFIG, AppConfig, AppConfigFactory} from '@icc/shared/config';;
import { WindowActiveConfiguration } from '@icc/window-door/types';
import { FramesService } from './frames.service';
import { Alignment, Frame } from '@icc/window-door/types';
import { core } from '@icc/shared/helpers';
import { ActiveSash } from '@icc/window-door/types';
import { AlignmentsService } from '../../layout/alignments.service';
import { TranslateService, EventBusService, BaseIssuesService } from '@icc/shared';

@Injectable()
export class FixSashThresholdService {
    constructor(
        private eventBusService: EventBusService,
        private issuesService: BaseIssuesService,
        private validationService: ValidationService,
        private translateService: TranslateService,
        private splitFrameService: SplitFrameService,
        private alignmentsService: AlignmentsService,
        @Inject(APP_CONFIG) private config: AppConfigFactory,
        private profilesService: ProfilesService
    ) {
        this.eventBusService.subscribe<any>(
            ['setLowThreshold', 'unsetLowThreshold', 'setSystem', 'changedSashes'],
            data => {
                this.validateThresholdAlignmentsAndFixIssues(
                    data.activeConfiguration as WindowActiveConfiguration,
                    data.value.action === 'joinedFrame'
                );
            }
        );
    }

    validateThresholdAlignmentsAndFixIssues(
        conf: WindowActiveConfiguration,
        notSplitFrames = false
    ) {
        if (this.validationService.isValidElements(conf, ['system', 'sashes', 'loadedProfiles'])) {
            const alignmentsToRemove = conf.Alignments.filter(alignment => {
                const alignmentProfile = this.profilesService.getProfile(alignment.profileId);
                const frame = FramesService.getFrameById(alignment.frameId, conf);
                return (
                    alignment.side === 'bottom'
                    && alignment.parallelAlignments.bottom.length === 0
                    && alignment.perpendicularAlignments.left.length === 0
                    && alignment.perpendicularAlignments.right.length === 0
                    && alignmentProfile.options.indexOf('alignment_threshold') > -1
                    && (!frame.lowThreshold
                        || conf.Sashes.some(
                            sash =>
                                sash.frameId === frame.id
                                && alignment.adjacentSashes.top.includes(sash.id)
                                && sash.type.type !== 'F'
                        )
                        || !alignmentProfile.systems.includes(Number(conf.System.id)))
                );
            });
            alignmentsToRemove.forEach(alignment =>
                this.removeBottomThresholdAlignment(alignment, conf)
            );
            this.setKickerWhenApplicable(conf);
            if (!notSplitFrames) {
                this.splitFrameService.splitLowThresholdFrameContainingFixAndSash(conf);
            }
            this.updateBottomFrameProfileSegments(conf);
            this.validKickerAvailablity(conf);
        }
    }

    private removeBottomThresholdAlignment(alignmentToRemove, conf: WindowActiveConfiguration) {
        const alignments: Alignment[] = conf.Alignments;
        const mullions = conf.Mullions;
        const sashes = conf.Sashes;

        if (alignmentToRemove.adjacentSashes.top.length > 0) {
            alignmentToRemove.adjacentSashes.top.forEach(sashId => {
                const nearSash = core.fIdO<any>(sashes, sashId);
                nearSash.nearAlignments.bottom = -1;
            });
        }
        if (alignmentToRemove.parallelAlignments.top.length > 0) {
            alignmentToRemove.parallelAlignments.top
                .map(nearAlignmentId => core.fIdO<Alignment>(alignments, nearAlignmentId))
                .forEach(nearAlignment => {
                    nearAlignment.parallelAlignments.bottom = nearAlignment.parallelAlignments.bottom.filter(
                        el => el !== alignmentToRemove.id
                    );
                });
        }

        if (alignmentToRemove.perpendicularMullions.left.length > 0) {
            alignmentToRemove.perpendicularMullions.left
                .map(nearMullionId => core.fIdO<any>(mullions, nearMullionId))
                .forEach(nearMullion => {
                    nearMullion.perpendicularAlignments.right = nearMullion.perpendicularAlignments.right.filter(
                        el => el !== alignmentToRemove.id
                    );
                });
        }
        if (alignmentToRemove.perpendicularMullions.right.length > 0) {
            alignmentToRemove.perpendicularMullions.right
                .map(nearMullionId => core.fIdO<any>(mullions, nearMullionId))
                .forEach(nearMullion => {
                    nearMullion.perpendicularAlignments.left = nearMullion.perpendicularAlignments.left.filter(
                        el => el !== alignmentToRemove.id
                    );
                });
        }

        const oldAlignmentIndex = alignments.findIndex(
            alignment => alignment.id === alignmentToRemove.id
        );

        alignments.splice(oldAlignmentIndex, 1);
        conf.Layout.changed = true;
        this.eventBusService.post({
            key: 'removedAlignment',
            value: {
                side: 'bottom',
            },
            conf
        });
    }

    private validKickerAvailablity(conf, showmodal = true) {
        const availKickers = this.alignmentsService.getThresholdAlignments(conf).length > 0;

        for (const frame of conf.Frames.filter(f => f.lowThreshold)) {
            const sashesFixOnLowThreshold = conf.Sashes.filter(
                sash =>
                    sash.type.type === 'F'
                    && sash.nearMullions.bottom === -1
                    && frame.id === sash.frameId
            );
            const sashesforKickers =
                sashesFixOnLowThreshold.length > 0
                && conf.Sashes.some(
                    sash =>
                        ['F', 'FF', 'OFF', 'K'].indexOf(sash.type.type) < 0
                        && sash.nearMullions.bottom === -1
                        && frame.id === sash.frameId
                );
            const noHasAlignmentThreshold = this.hasNotThresholdAlignment(
                conf,
                frame,
                sashesFixOnLowThreshold
            );

            if (
                (!availKickers || noHasAlignmentThreshold) &&
                conf.System.fix_and_low_threshold_behavior !== 'split-bottom-frame-profile'
                && sashesforKickers
                && frame.lowThreshold
            ) {
                this.issuesService.simpleRegister(
                    'no-kicker',
                    'Niski próg nie może być zamontowany pod stałym szkleniem. Konstrukcję należy skonfigurować w dwóch ramach lub należy wyłączyć niski próg.',
                    this.translateService.instant(
                        'WINDOW|Niski próg nie może być zamontowany pod stałym szkleniem. Konstrukcję należy skonfigurować w dwóch ramach lub należy wyłączyć niski próg.'
                    ),
                    conf,
                    {
                        showMessage: showmodal,
                        logLevel: IssueLevel.NONE
                    }
                );
                return false;
            }
        }
        this.issuesService.unregister('no-kicker', conf);
        return true;
    }

    private setKickerWhenApplicable(conf: WindowActiveConfiguration) {
        if (!this.validationService.isValidElements(conf, ['system', 'sashes', 'loadedProfiles'])) {
            return;
        }

        conf.Kicker = false;

        const thresholdKickers = this.alignmentsService.getThresholdAlignments(conf);
        conf.Frames.filter(f => f.lowThreshold).forEach(frame => {
            const sashesFixOnLowThreshold = conf.Sashes.filter(
                sash =>
                    sash.type.type === 'F'
                    && sash.nearMullions.bottom === -1
                    && frame.id === sash.frameId
            );
            const sashesforKickers =
                sashesFixOnLowThreshold.length > 0
                && conf.Sashes.some(
                    sash =>
                        ['F', 'FF', 'OFF', 'K'].indexOf(sash.type.type) < 0
                        && sash.nearMullions.bottom === -1
                        && frame.id === sash.frameId
                );

            const noHasAlignmentThreshold = this.hasNotThresholdAlignment(
                conf,
                frame,
                sashesFixOnLowThreshold
            );

            const hasLowThreshold = frame.lowThreshold;
            conf.Kicker =
                conf.Kicker
                || (conf.System.fix_and_low_threshold_behavior === 'threshold-alignment'
                    && hasLowThreshold
                    && thresholdKickers.length > 0
                    && sashesforKickers);

            if (conf.Kicker && noHasAlignmentThreshold) {
                sashesFixOnLowThreshold.forEach(sash => {
                    if (
                        sash.nearAlignments.bottom === -1
                        && sash.nearAlignments.left === -1
                        && sash.nearAlignments.right === -1
                    ) {
                        const selectedProfile = thresholdKickers[0];
                        this.alignmentsService.putAlignmentInField(
                            selectedProfile,
                            sash,
                            conf,
                            'bottom'
                        );
                    }
                });
            }
        });
    }

    private hasNotThresholdAlignment(
        conf: WindowActiveConfiguration,
        frame: Frame,
        sashesFixOnLowThreshold: ActiveSash[]
    ) {
        return (
            conf.Alignments == null
            || conf.Alignments.length === 0
            || !conf.Alignments.some(alignment => {
                const alignmentProfile = this.profilesService.getProfile(alignment.profileId);
                return (
                    alignment.side === 'bottom'
                    && alignment.frameId === frame.id
                    && alignment.perpendicularAlignments.left.length === 0
                    && alignment.perpendicularAlignments.right.length === 0
                    && sashesFixOnLowThreshold.some(
                        sash => sash.nearAlignments.bottom === alignment.id
                    )
                    && alignmentProfile.options.indexOf('alignment_threshold') > -1
                );
            })
        );
    }


    private updateBottomFrameProfileSegments(conf: WindowActiveConfiguration) {
        let updatedSplitFrame = false;
        for (const frame of conf.Frames) {
            if (conf.System.fix_and_low_threshold_behavior !== 'split-bottom-frame-profile' ||
                !frame.lowThreshold
            ) {
                if (frame.splitBottom) {
                    frame.splitBottom = false;
                    updatedSplitFrame = true;
                }
                this.profilesService.refreshBottomFrameProfileSegments(frame, conf);
                continue;
            }

            const sashesFixOnLowThreshold = conf.Sashes.filter(
                sash =>
                    sash.type.type === 'F'
                    && sash.nearMullions.bottom === -1
                    && frame.id === sash.frameId
            );

            if (sashesFixOnLowThreshold.length === 0) {
                if (frame.splitBottom) {
                    frame.splitBottom = false;
                    updatedSplitFrame = true;
                }
                this.profilesService.refreshBottomFrameProfileSegments(frame, conf);
                continue;
            }

            if (!frame.splitBottom) {
                frame.splitBottom = true;
                updatedSplitFrame = true;
            }
            this.profilesService.refreshBottomFrameProfileSegments(frame, conf);
        }

        if (updatedSplitFrame) {
            this.eventBusService.post({
                key: 'updatedSplitFrame',
                value: {},
                conf
            });
        }
    }
}
